import PostCard from "@/components/Post/Card";
import api from "@/libs/api";
import {
  Box,
  Button,
  Grid,
  GridItem,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export default function CreatorPosts({ creatorId, isOwner = false }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const {
    data,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ["creator-posts", creatorId],
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      if (!creatorId) return { data: [], paginator: { next: null } };
      const res = await api.getCreatorPosts(creatorId, pageParam);
      return res || { data: [], paginator: { next: null } };
    },
    getNextPageParam: (lastPage) =>
      lastPage?.paginator?.next || lastPage?.next || undefined,
    enabled: !!creatorId,
  });

  const items = useMemo(() => {
    const pages = data?.pages || [];
    return pages.flatMap((p) => p?.data || p?.items || []);
  }, [data]);

  const handleUnpublish = useCallback(
    async (postId) => {
      try {
        await api.unpublishPost(postId);
        toast({
          title: "貼文已下架",
          status: "success",
          duration: 3000,
        });
        // Refresh the posts list
        queryClient.invalidateQueries({ queryKey: ["creator-posts", creatorId] });
      } catch (error) {
        console.error("Failed to unpublish post:", error);
        toast({
          title: "下架失敗",
          description: error.message || "請稍後再試",
          status: "error",
          duration: 3000,
        });
      }
    },
    [creatorId, queryClient, toast]
  );

  return (
    <Box>
      {error && (
        <Text color="orange.300" mb={4}>
          Posts endpoint not available yet.
        </Text>
      )}
      <Grid templateColumns={{ base: "1fr", md: "repeat(1, 1fr)" }} gap={4}>
        {items.map((post) => (
          <GridItem key={post.id}>
            <VStack align="stretch" gap={3}>
              <PostCard
                post={post}
                onClick={() => navigate(`/posts/${post.id}`)}
                onUnpublish={isOwner ? handleUnpublish : undefined}
              />
            </VStack>
          </GridItem>
        ))}
      </Grid>
      <VStack mt={6}>
        {hasNextPage && (
          <Button
            onClick={() => fetchNextPage()}
            isLoading={isFetchingNextPage}
            variant="themed-outline"
          >
            Load more
          </Button>
        )}
        {!items.length && (
          <Text color="gray.400">尚無貼文, 訂閱後即可接收最新通知。</Text>
        )}
      </VStack>
    </Box>
  );
}


